import React from "react";
import { isUpgradeTicketInventoryShortage } from "utility/conditionalTicket";

import type { TicketConfig } from "@spwn/types/firebase/firestore";
import type { PurchaseTicketData } from "modules/ticket";

type TicketQuantitySelectBoxProps = {
  ticket: PurchaseTicketData;
  ticketConfig: TicketConfig;
  isLotteryTicketSellingMode: boolean;
  purchaseMaxLimit: number;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
};

export const TicketQuantitySelectBox: React.FC<
  TicketQuantitySelectBoxProps
> = ({
  ticket,
  ticketConfig,
  isLotteryTicketSellingMode,
  purchaseMaxLimit,
  onChange,
}) => {
  /**
   * アップグレード販売では、購入しない 0 または前提チケット購入済み枚数だけを選択肢にする。
   * ただし通常購入で必要枚数より在庫が少ない場合は、購入数を 0 のみに制限する。
   * 抽選申込は従来どおり在庫を参照しない。
   *
   * requiredTicketType === "upgrade" の場合、早期return しているため、ここに到達するのは "discount" または undefined の場合のみ。
   * なので今後、 upgrade で subClass がある場合が出てきた場合は、別途条件分岐が必要になる。
   * https://github.com/balus-co-ltd/spwn/pull/12314#discussion_r3272751153
   *
   * hasSubClass がどう言う意図で存在しているのか正確にはわからないため確認は行う。
   * @see https://balus3d.slack.com/archives/C01C7KHJ2J0/p1779336666582609
   */
  if (
    ticket.requiredTicketType === "upgrade" &&
    ticket.purchaseRequiredTicketCount !== undefined
  ) {
    // 抽選申込は従来どおり在庫を参照せず、通常購入だけ在庫不足時の購入数を 0 に制限する。
    const requiredCount =
      !isLotteryTicketSellingMode && isUpgradeTicketInventoryShortage(ticket)
        ? 0
        : ticket.purchaseRequiredTicketCount;
    return (
      <select defaultValue={0} onChange={onChange}>
        <option id={ticket._id} value={0}>
          0
        </option>
        {requiredCount > 0 && (
          <option id={ticket._id} value={requiredCount}>
            {requiredCount}
          </option>
        )}
      </select>
    );
  }

  /**
   * 選択可能な最大枚数の計算。
   * ticketConfig 側のイベント全体上限とチケット単体上限を見たうえで、
   * 99999 のような極端な値がループに渡されないよう purchaseMaxLimit でも丸める。
   */
  const maxSelectableCount = (() => {
    const baseLimit = Math.min(
      ticket.purchaseLimit,
      ticketConfig.config?.maxTicketPurchase ?? purchaseMaxLimit
    );
    const cappedLimit = Math.min(baseLimit, purchaseMaxLimit);

    // ディスカウント販売では、購入制限に加えて残り割引枠も選択可能枚数の上限にする。
    if (
      ticket.requiredTicketType === "discount" &&
      ticket.purchaseRequiredTicketCount !== undefined
    ) {
      const discountLimit = Math.min(
        cappedLimit,
        ticket.purchaseRequiredTicketCount
      );

      if (isLotteryTicketSellingMode) {
        return discountLimit;
      }

      return Math.min(discountLimit, ticket.remaining);
    }

    if (isLotteryTicketSellingMode) {
      return cappedLimit;
    }

    return Math.min(cappedLimit, ticket.remaining);
  })();

  const isSubClass =
    ticketConfig.hasSubClass &&
    ticket.subClassName &&
    ticket.subClassName.length > 0;

  const defaultValue = isSubClass ? `0_${ticket.classGroupId ?? ""}` : "0";

  const options = Array.from({ length: maxSelectableCount + 1 }, (_, i) => {
    if (isSubClass) {
      return (
        <option
          key={`${ticket._id}_${i}`}
          value={`${i}_${ticket.classGroupId ?? ""}`}
        >
          {i}
        </option>
      );
    }
    return (
      <option id={ticket._id} key={`${ticket._id}_${i}`} value={i}>
        {i}
      </option>
    );
  });

  return (
    <select defaultValue={defaultValue} onChange={onChange}>
      {options}
    </select>
  );
};
